import { Button, Dropdown, Input, Modal } from "@amzn/alchemy-components-react";
import { useTranslation } from "react-i18next";
import { AlertType, ModalCloseSource, TenantID, UserType } from "src/common/enums";
import { AlertBar } from "src/components/alert-bar";
import React, { useEffect, useState } from "react";
import { logger } from "src/logger";
import { isEmptyString } from "src/common/functions";
import { useLazyQuery } from "@apollo/client";
import { GET_USER_INFO_BY_ALIAS_QUERY } from "src/common/gql-operations";
import { NETWORK_ONLY } from "src/common/constants";

import initialMetricsPublisher from "src/metrics";
import * as KatalMetrics from "@amzn/katal-metrics";

const cloudWatchDimensions = [
    new KatalMetrics.Metric.String('modal', 'add-user'),
];

const additionalMetricsContext = new KatalMetrics.Context({ cloudWatchDimensions });
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);

interface AddUserModalProps {
    open: boolean;
    setOpen: CallableFunction;
    scope: string;
}

interface FormState {
    systemAccessCredential: string;
    selectedUserType: string;
}

interface AlertState {
    type?: AlertType;
    header?: string;
    message?: string;
}

export const AddUserModal = (props: AddUserModalProps) => {
    const { t } = useTranslation();

    const [formState, setFormState] = useState<FormState>({
        systemAccessCredential: '',
        selectedUserType: ''
    });

    const isValid = !isEmptyString(formState.systemAccessCredential)
        && !isEmptyString(formState.selectedUserType);

    const [alertState, setAlertState] = useState<AlertState>({
        type: undefined,
        header: undefined,
        message: undefined
    });

    const [getUserInfoByAlias, { loading }] = useLazyQuery(GET_USER_INFO_BY_ALIAS_QUERY, {
        fetchPolicy: NETWORK_ONLY,
        onCompleted: (data) => {
            logger.info("Call to getUserInfoByAlias succeeded", data);
            graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.SUCCESS', 1);
            // TODO: Handle response in SIM: https://sim.amazon.com/issues/AFTI-4504
        },
        onError: error => {
            logger.error("Call to getUserInfoByAlias failed!", error);
            graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.ERROR', 1);
            showErrorAlert('Fail to get the user info from system credentials', error.message);
        }
    });

    useEffect(() => {
        if (!props.open) {
            resetForm();
        }
    }, [props.open]);

    const handleModalClose = (source: ModalCloseSource) => {
        logger.info(`Add User Modal closing from: ${source}`);
        resetForm();
        props.setOpen(false);
    };

    const resetForm = () => {
        setFormState({
            systemAccessCredential: '',
            selectedUserType: ''
        });
        resetAlertBar();
    };

    const resetAlertBar = () => {
        setAlertState({
            type: undefined,
            header: undefined,
            message: undefined
        });
    };

    const showErrorAlert = (header: string, message: string) => {
        setAlertState({
            type: AlertType.error,
            header,
            message
        });
    };

    const handleSystemAccessCredentialInput = (value: string) => {
        setFormState({
            ...formState,
            systemAccessCredential: value
        });
    };

    const handleUserTypeSelection = (userType: string) => {
        logger.info(`Selected User Type: ${userType}`);
        setFormState({
            ...formState,
            selectedUserType: userType
        });
    };

    const handleSubmit = async () => {
        if (!isValid) {
            showErrorAlert(
                t('invalid-form'),
                t('please-fill-in-all-fields')
            );
            return;
        }

        logger.info(`Submitting with SAC: ${formState.systemAccessCredential}, UserType: ${formState.selectedUserType}, Scope: ${props.scope}`);
        
        graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.INVOCATION', 1);
        getUserInfoByAlias({
            variables: {
                getUserInfoByAliasInput: {
                    tenantId: TenantID.AFTX,
                    aliases: [formState.systemAccessCredential]
                }
            }
        });
    };

    const userTypeOptions = [
        { label: t('select-user-type'), value: '' },
        { label: UserType.ASSOCIATE, value: UserType.ASSOCIATE },
        { label: UserType.USER_ADMIN_PRIMARY, value: UserType.USER_ADMIN_PRIMARY },
        { label: UserType.USER_ADMIN_SECONDARY, value: UserType.USER_ADMIN_SECONDARY },
        { label: UserType.ABBM_SECONDARY, value: UserType.ABBM_SECONDARY }
    ];

    return (
        <Modal
            id="add-user-modal"
            className="mb-1 add-user-modal"
            header={t('add-user-to-scope', { scope: props.scope })}
            open={props.open}
            size="large"
            onClose={(e: React.MouseEvent) => {
                if (e.target === e.currentTarget) {
                    handleModalClose(ModalCloseSource.OVERLAY);
                } else if ((e.target as HTMLElement).closest('.alchemy-modal-close-button')) {
                    handleModalClose(ModalCloseSource.CROSS);
                }
            }}
        >
            <div className="container-fluid" onClick={(e) => e.stopPropagation()} style={{ paddingTop: '2rem', paddingLeft: '4rem', paddingRight: '4rem' }}>
                <div className="row mb-3">
                    <div className="col">
                        <Input
                            id="system-access-credential-input"
                            label={t('system-access-credentials')}
                            type="text"
                            value={formState.systemAccessCredential}
                            onInput={e => handleSystemAccessCredentialInput(e.target.value)}
                        />
                    </div>
                </div>

                <div className="row mb-3">
                    <div className="col">
                        <Dropdown
                            id="user-type-select"
                            label={t('select-user-type')}
                            options={userTypeOptions}
                            onChange={e => handleUserTypeSelection(e.target.value)}
                            value={formState.selectedUserType}
                        />
                    </div>
                </div>

                <div className="row d-flex justify-content-center" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
                    <Button
                        id="add-user-modal-cancel-button"
                        size="lg"
                        label={t('cancel')}
                        onClick={() => handleModalClose(ModalCloseSource.CANCEL)}
                        className="mx-1"
                    />
                    <Button
                        id="add-user-modal-submit-button"
                        size="lg"
                        label={t('submit')}
                        variant="action"
                        disabled={!isValid || loading}
                        onClick={handleSubmit}
                        className="mx-1"
                    />
                </div>
            </div>
            {alertState.message && (
                <AlertBar
                    id="add-user-modal-alert-bar"
                    result={alertState.type!}
                    dismissible={alertState.type !== AlertType.error}
                    header={alertState.header!}
                    message={alertState.message}
                    reset={resetAlertBar}
                />
            )}
        </Modal>
    );
};
